import React, { useState, useEffect } from 'react';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { Plus, Edit, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { BuildingOption } from '../../api/feeCategoriesApi';
import {
  feedbackCategoriesApi,
  FeedbackCategory,
} from '../../api/feedbacksApi';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import FeedbackWorkflowEditor, { WorkflowStepForm } from '../../components/feedbacks/FeedbackWorkflowEditor';

const FeedbackCategoryConfig: React.FC = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const [categories, setCategories] = useState<FeedbackCategory[]>([]);
  const [buildings, setBuildings] = useState<BuildingOption[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FeedbackCategory | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    code: '',
    buildingId: '',
    isActive: true,
  });
  const [activeTab, setActiveTab] = useState<'general' | 'workflow'>('general');
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStepForm[]>([]);
  const [savingWorkflow, setSavingWorkflow] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

  const reorderList = <T,>(list: T[], fromIndex: number, toIndex: number): T[] => {
    const result = [...list];
    const [removed] = result.splice(fromIndex, 1);
    result.splice(toIndex, 0, removed);
    return result;
  };

  const persistCategoryOrder = async (ordered: FeedbackCategory[]) => {
    if (!selectedBuildingId) return;
    try {
      setSavingOrder(true);
      const updated = await feedbackCategoriesApi.reorderCategories(
        selectedBuildingId,
        ordered.map((c) => c.id),
      );
      setCategories(updated);
      toast.success(t('orderSaved'));
    } catch (error) {
      console.error('Error saving category order:', error);
      toast.error(t('errorUpdatingFeedbackCategory'));
      fetchCategories();
    } finally {
      setSavingOrder(false);
    }
  };

  const moveCategory = async (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= categories.length) return;
    const reordered = reorderList(categories, index, targetIndex);
    setCategories(reordered);
    await persistCategoryOrder(reordered);
  };

  useEffect(() => {
    fetchBuildings();
  }, []);

  useEffect(() => {
    if (selectedBuildingId) {
      fetchCategories();
    }
  }, [selectedBuildingId]);

  const fetchBuildings = async () => {
    try {
      console.log('Fetching buildings from:', `${API_BASE_URL}/buildings`); // Debug log
      const response = await fetch(`${API_BASE_URL}/buildings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Buildings received:', data); // Debug log
        setBuildings(data);
        setSelectedBuildingId((prev) => applyDefaultBuildingSelection(data, user, prev));
      } else {
        console.error('Failed to fetch buildings:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/feedback-categories?buildingId=${selectedBuildingId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      } else {
        toast.error(t('errorLoadingFeedbackCategories'));
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error(t('errorLoadingFeedbackCategories'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedBuildingId) {
      toast.error(t('pleaseSelectBuilding'));
      return;
    }
    
    if (!formData.name.trim()) {
      toast.error(t('pleaseEnterCategoryName'));
      return;
    }
    
    if (!formData.code.trim()) {
      toast.error(t('pleaseEnterCategoryCode'));
      return;
    }

    const submitData = {
      ...formData,
      buildingId: selectedBuildingId,
    };

    console.log('Submitting feedback category:', submitData); // Debug log

    try {
      const url = editingCategory 
        ? `${API_BASE_URL}/feedback-categories/${editingCategory.id}`
        : `${API_BASE_URL}/feedback-categories`;
      
      const method = editingCategory ? 'PATCH' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        toast.success(editingCategory 
          ? t('feedbackCategoryUpdatedSuccess') 
          : t('feedbackCategoryCreatedSuccess')
        );
        setShowForm(false);
        setEditingCategory(null);
        resetForm();
        fetchCategories();
      } else {
        const errorData = await response.json();
        console.error('Error response:', errorData); // Debug log
        toast.error(errorData.message || (editingCategory 
          ? t('errorUpdatingFeedbackCategory') 
          : t('errorCreatingFeedbackCategory'))
        );
      }
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(editingCategory 
        ? t('errorUpdatingFeedbackCategory') 
        : t('errorCreatingFeedbackCategory')
      );
    }
  };

  const handleEdit = async (category: FeedbackCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      nameEn: category.nameEn || '',
      code: category.code,
      buildingId: category.buildingId,
      isActive: category.isActive,
    });
    setActiveTab('general');
    try {
      const steps = await feedbackCategoriesApi.getWorkflow(category.id);
      setWorkflowSteps(
        steps.length > 0
          ? steps.map((s) => ({
              id: s.id,
              stepOrder: s.stepOrder,
              stepName: s.stepName,
              statusDescription: s.statusDescription || '',
              feedbackStatus: s.feedbackStatus,
              permissionGroupId: s.permissionGroupId || '',
              slaDuration: s.slaDuration,
              slaUnit: s.slaUnit,
              reminderDuration: s.reminderDuration,
              reminderUnit: s.reminderUnit,
              assigneeIds: s.assignees.map((a) => a.user.id),
            }))
          : [{
              stepOrder: 1,
              stepName: 'Tiếp nhận',
              statusDescription: '',
              feedbackStatus: 'PENDING_CONFIRMATION',
              permissionGroupId: '',
              slaDuration: 0.5,
              slaUnit: 'HOURS',
              reminderDuration: 10,
              reminderUnit: 'MINUTES',
              assigneeIds: [],
            }],
      );
    } catch {
      setWorkflowSteps([]);
    }
    setShowForm(true);
  };

  const handleDelete = async (category: FeedbackCategory) => {
    if (!window.confirm(t('confirmDeleteFeedbackCategory'))) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/feedback-categories/${category.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success(t('feedbackCategoryDeletedSuccess'));
        fetchCategories();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || t('errorDeletingFeedbackCategory'));
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(t('errorDeletingFeedbackCategory'));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      nameEn: '',
      code: '',
      buildingId: selectedBuildingId,
      isActive: true,
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCategory(null);
    setActiveTab('general');
    setWorkflowSteps([]);
    resetForm();
  };

  const handleSaveWorkflow = async () => {
    if (!editingCategory) return;
    setSavingWorkflow(true);
    try {
      await feedbackCategoriesApi.saveWorkflow(editingCategory.id, workflowSteps);
      toast.success(t('workflowSavedSuccess'));
      fetchCategories();
    } catch {
      toast.error(t('errorSavingWorkflow'));
    } finally {
      setSavingWorkflow(false);
    }
  };

  const handleCreateDefaultCategories = async () => {
    if (!selectedBuildingId) {
      toast.error(t('pleaseSelectBuilding'));
      return;
    }

    if (!window.confirm(t('confirmCreateDefaultFeedbackCategories'))) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/feedback-categories/create-default`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ buildingId: selectedBuildingId }),
      });

      if (response.ok) {
        toast.success(t('defaultFeedbackCategoriesCreatedSuccess'));
        fetchCategories();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || t('errorCreatingDefaultFeedbackCategories'));
      }
    } catch (error) {
      console.error('Error creating default categories:', error);
      toast.error(t('errorCreatingDefaultFeedbackCategories'));
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('feedbackCategoryConfig')}</h1>
        <div className="flex gap-3">
          <button
            onClick={handleCreateDefaultCategories}
            disabled={!selectedBuildingId}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus size={20} />
            {t('createDefaultFeedbackCategories')}
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus size={20} />
            {t('addFeedbackCategory')}
          </button>
        </div>
      </div>

      {/* Building Selection */}
      {shouldShowBuildingSelector(buildings) && (
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('selectBuilding')}
        </label>
        <select
          value={selectedBuildingId}
          onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
          className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{t('selectBuildingPlaceholder')}</option>
          {buildings.map((building) => (
            <option key={building.id} value={building.id}>
              {building.name}
            </option>
          ))}
        </select>
      </div>
      )}

      {/* Categories Table */}
      {selectedBuildingId && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <p className="px-6 py-3 text-sm text-gray-600 border-b border-gray-100">
            {t('dragToReorder')}
            {savingOrder ? ` (${t('loading')}...)` : ''}
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('stt')}
                  </th>
                  <th className="px-4 py-3 w-12" aria-label={t('dragToReorder')} />
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('feedbackCategoryName')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('feedbackCategoryNameEn')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('feedbackCategoryCode')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('feedbackResponsibleStaff')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('totalFeedbacks')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                      {t('loading')}...
                    </td>
                  </tr>
                ) : categories.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                      {t('noFeedbackCategories')}
                    </td>
                  </tr>
                ) : (
                  categories.map((category, index) => (
                    <tr key={category.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex flex-col gap-0.5">
                          <button
                            type="button"
                            onClick={() => moveCategory(index, 'up')}
                            disabled={index === 0 || savingOrder}
                            className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                          >
                            <ChevronUp size={18} />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveCategory(index, 'down')}
                            disabled={index === categories.length - 1 || savingOrder}
                            className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                          >
                            <ChevronDown size={18} />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {category.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {category.nameEn || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                          {category.code}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                        {category.responsibleStaff && category.responsibleStaff.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {category.responsibleStaff.map((staff) => (
                              <span key={staff.id} className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs">
                                {staff.name}
                              </span>
                            ))}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {category._count?.feedbacks ?? 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          category.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {category.isActive ? t('active') : t('inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(category)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(category)}
                            className="text-red-600 hover:text-red-900"
                            disabled={(category._count?.feedbacks ?? 0) > 0}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`bg-white rounded-lg p-6 w-full ${editingCategory ? 'max-w-4xl max-h-[90vh] overflow-y-auto' : 'max-w-md'}`}>
            <h2 className="text-xl font-bold mb-4">
              {editingCategory ? t('editFeedbackCategory') : t('addFeedbackCategory')}
            </h2>

            {editingCategory && (
              <div className="flex border-b mb-4">
                <button
                  type="button"
                  onClick={() => setActiveTab('general')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    activeTab === 'general'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500'
                  }`}
                >
                  {t('generalInfoTab')}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('workflow')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                    activeTab === 'workflow'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500'
                  }`}
                >
                  {t('processingWorkflowTab')}
                </button>
              </div>
            )}

            {(activeTab === 'general' || !editingCategory) && (
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('feedbackCategoryName')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('enterCategoryName')}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('feedbackCategoryNameEn')}
                </label>
                <input
                  type="text"
                  value={formData.nameEn}
                  onChange={(e) => setFormData({ ...formData, nameEn: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('enterCategoryNameEn')}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('feedbackCategoryCode')} *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('enterCategoryCode')}
                />
              </div>

              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">{t('active')}</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingCategory ? t('update') : t('create')}
                </button>
              </div>
            </form>
            )}

            {editingCategory && activeTab === 'workflow' && (
              <div>
                <FeedbackWorkflowEditor
                  steps={workflowSteps}
                  onChange={setWorkflowSteps}
                  buildingId={selectedBuildingId}
                />
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveWorkflow}
                    disabled={savingWorkflow}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {savingWorkflow ? `${t('loading')}...` : t('save')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackCategoryConfig; 
